import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useRouter } from '@popety-io/ui'
import { useMap } from '../../contexts/MapContext'
import { useBuildingsUrl } from '../hooks/useBuildingsUrl'
import { buildingSearchService } from '../services'
import { buildingTagsService } from '../services/buildingTagsService'
import type {
  BuildingsState,
  BuildingsActions,
  Building,
  BuildingSearchFilters,
  BuildingSearchSort,
} from '../types/buildingFilters.types'
import type { TagWithUserInfo } from '@popety-io/types'
import {
  applyBuildingFiltersFromAI,
  AIFilterResult,
} from '../services/aiFilterService'

interface BuildingsContextType extends BuildingsState, BuildingsActions {}

const BuildingsContext = createContext<BuildingsContextType | undefined>(
  undefined
)

type BuildingsAction =
  | { type: 'SET_BUILDINGS'; payload: Building[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_TOTAL'; payload: number }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_FILTERS'; payload: BuildingSearchFilters }
  | { type: 'SET_DEBOUNCED_FILTERS'; payload: BuildingSearchFilters }
  | { type: 'SET_SORT'; payload: BuildingSearchSort[] }
  | { type: 'SET_SELECTED_BUILDING_ID'; payload: string | undefined }
  | { type: 'SET_FILTER_MODE'; payload: 'manual' | 'ai' }
  | { type: 'SET_AI_FILTER_RESULT'; payload: AIFilterResult | null }
  | { type: 'SET_AVAILABLE_TAGS'; payload: TagWithUserInfo[] }
  | { type: 'SET_TAGS_LOADING'; payload: boolean }
  | { type: 'CLEAR_FILTERS' }
  | {
      type: 'SYNC_FROM_URL'
      payload: {
        filters: BuildingSearchFilters
        sort: BuildingSearchSort[]
        page: number
        pageSize: number
      }
    }

// Default sort field for buildings - different from lands which uses 'area'
const DEFAULT_BUILDING_SORT = [
  { field: 'building_year', order: 'desc' as const },
]

const initialState: BuildingsState = {
  buildings: [],
  loading: false,
  error: null,
  total: 0,
  currentPage: 1,
  pageSize: 20,
  filters: {},
  debouncedFilters: {},
  sort: DEFAULT_BUILDING_SORT,
  selectedBuildingId: undefined,
  filterMode: 'manual',
  aiFilterResult: null,
  availableTags: [],
  tagsLoading: false,
  fields: [
    'id',
    'building_id',
    'address_full',
    'egid',
    // 'geo_polygon_wkt',
    // Additional fields needed for layer coloring or display in tooltip
    'area',
    'popety_classification',
    'category',
    'type',
    'building_year',
    'floor_nb',
    'unit_nb',
    'dwellings_nb',
    'protected_rank',
    'solar_average_exposure',
    'power_source_heater',
    'power_source_water_heater',
    'noise_exposure',
    'lake_view',
    'mountain_view',
    'public_transport_quality',
    'travel_time_to_centers',
    'building_period',
    'renovation_period',
    'listing_rent_last_year',
    'listing_sale_last_year',
    'lands_building_permit_last_year',
    'lands_transaction_last_year',

    // 'geo_polygon',
  ], // Default fields for tiles including layer coloring fields
}

function buildingsReducer(
  state: BuildingsState,
  action: BuildingsAction
): BuildingsState {
  switch (action.type) {
    case 'SET_BUILDINGS':
      return { ...state, buildings: action.payload }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_TOTAL':
      return { ...state, total: action.payload }
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, currentPage: 1 }
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 1 }
    case 'SET_DEBOUNCED_FILTERS':
      return { ...state, debouncedFilters: action.payload, currentPage: 1 }
    case 'SET_SORT':
      return { ...state, sort: action.payload }
    case 'SET_SELECTED_BUILDING_ID':
      return { ...state, selectedBuildingId: action.payload }
    case 'SET_FILTER_MODE':
      return { ...state, filterMode: action.payload }
    case 'SET_AI_FILTER_RESULT':
      return { ...state, aiFilterResult: action.payload }
    case 'SET_AVAILABLE_TAGS':
      return { ...state, availableTags: action.payload }
    case 'SET_TAGS_LOADING':
      return { ...state, tagsLoading: action.payload }
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {},
        debouncedFilters: {},
        currentPage: 1,
        aiFilterResult: null,
      }
    case 'SYNC_FROM_URL': {
      // Only update if values actually changed to prevent loops
      const needsUpdate =
        JSON.stringify(state.filters) !==
          JSON.stringify(action.payload.filters) ||
        JSON.stringify(state.sort) !== JSON.stringify(action.payload.sort) ||
        state.currentPage !== action.payload.page ||
        state.pageSize !== action.payload.pageSize

      if (!needsUpdate) return state

      return {
        ...state,
        filters: action.payload.filters,
        debouncedFilters: action.payload.filters,
        sort: action.payload.sort,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize,
      }
    }
    default:
      return state
  }
}

interface BuildingsProviderProps {
  children: ReactNode
}

export const BuildingsProvider: React.FC<BuildingsProviderProps> = ({
  children,
}) => {
  const { query } = useRouter()
  const { viewMode, propertyPanelOpen } = useMap()
  const { urlState, updateFilters, updateSort, updatePagination } =
    useBuildingsUrl(propertyPanelOpen)

  // Get current mode from URL
  const currentMode = (query.mode as 'plot' | 'building') || 'plot'

  // Initialize state from URL
  const initialStateFromUrl: BuildingsState = {
    ...initialState,
    filters: urlState.filters,
    debouncedFilters: urlState.filters,
    sort: urlState.sort.length > 0 ? urlState.sort : DEFAULT_BUILDING_SORT,
    currentPage: urlState.page,
    pageSize: urlState.pageSize,
    filterMode: 'manual',
    aiFilterResult: null,
  }

  const [state, dispatch] = useReducer(buildingsReducer, initialStateFromUrl)
  const isUpdatingFromUrl = useRef(false)
  const lastUrlStateRef = useRef(JSON.stringify(urlState))

  const setBuildings = (buildings: Building[]) => {
    dispatch({ type: 'SET_BUILDINGS', payload: buildings })
  }

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading })
  }

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }

  const setTotal = (total: number) => {
    dispatch({ type: 'SET_TOTAL', payload: total })
  }

  const setCurrentPage = (page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page })
    if (!isUpdatingFromUrl.current) {
      updatePagination(page, state.pageSize)
    }
  }

  const setPageSize = (size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size })
    if (!isUpdatingFromUrl.current) {
      updatePagination(1, size) // Reset to page 1 when changing page size
    }
  }

  const setFilters = useCallback(
    (filters: BuildingSearchFilters) => {
      dispatch({ type: 'SET_FILTERS', payload: filters })
      if (!isUpdatingFromUrl.current) {
        updateFilters(filters)
      }
    },
    [updateFilters]
  )

  const setSort = (sort: BuildingSearchSort[]) => {
    dispatch({ type: 'SET_SORT', payload: sort })
    if (!isUpdatingFromUrl.current) {
      updateSort(sort)
    }
  }

  const setSelectedBuildingId = (id?: string) => {
    dispatch({ type: 'SET_SELECTED_BUILDING_ID', payload: id })
  }

  const clearFilters = () => {
    dispatch({ type: 'CLEAR_FILTERS' })
    if (!isUpdatingFromUrl.current) {
      updateFilters({})
    }
  }

  const setFilterMode = (mode: 'manual' | 'ai') => {
    dispatch({ type: 'SET_FILTER_MODE', payload: mode })
  }

  const updateFilter = useCallback(
    <K extends keyof BuildingSearchFilters>(
      key: K,
      value: BuildingSearchFilters[K]
    ) => {
      const newFilters = { ...state.filters, [key]: value }
      setFilters(newFilters)
    },
    [state.filters, setFilters]
  )

  const updateMultipleFilters = useCallback(
    (updates: Partial<BuildingSearchFilters>) => {
      const newFilters = { ...state.filters, ...updates }
      setFilters(newFilters)
    },
    [state.filters, setFilters]
  )

  // Debounce filter changes for text inputs
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set new timeout to update debounced filters after 500ms
    debounceTimeoutRef.current = setTimeout(() => {
      dispatch({ type: 'SET_DEBOUNCED_FILTERS', payload: state.filters })
    }, 500)

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [state.filters])

  const searchBuildings = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)

    try {
      const request = {
        filters: state.debouncedFilters,
        limit: state.pageSize,
        offset: (state.currentPage - 1) * state.pageSize,
        sort: state.sort,
      }

      const response = await buildingSearchService.getBuildingsForTable(request)

      setBuildings(response.buildings as Building[])
      setTotal(response.total)
    } catch (error) {
      console.error('Error searching buildings:', error)
      setError(
        error instanceof Error
          ? error.message
          : 'An error occurred while searching buildings'
      )
    } finally {
      setLoading(false)
    }
  }, [state.debouncedFilters, state.pageSize, state.currentPage, state.sort])

  const loadBuilding = async (id: string): Promise<Building | null> => {
    try {
      return await buildingSearchService.getBuildingForDetail(id)
    } catch (error) {
      console.error('Error loading building:', error)
      setError(
        error instanceof Error
          ? error.message
          : 'An error occurred while loading building'
      )
      return null
    }
  }

  // Trigger search manually (for ListView)
  const triggerSearch = useCallback(() => {
    // Force a search regardless of view mode (for manual triggers)
    searchBuildings()
  }, [searchBuildings])

  // AI Filter Methods
  const applyAIFilters = useCallback(
    (aiResponse: string | object, mergeWithExisting: boolean = true) => {
      try {
        const result = applyBuildingFiltersFromAI(
          aiResponse,
          mergeWithExisting ? state.filters : {},
          { mergeWithExisting }
        )

        // Store the AI filter result for user feedback
        dispatch({ type: 'SET_AI_FILTER_RESULT', payload: result })

        if (result.success && Object.keys(result.filters).length > 0) {
          // Apply the validated filters
          setFilters(result.filters)
          setFilterMode('ai')
        }

        return result
      } catch (error) {
        const errorResult: AIFilterResult = {
          success: false,
          filters: state.filters,
          errors: [
            `Failed to apply AI filters: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          ],
          appliedFilters: [],
          originalResponse: aiResponse,
        }

        dispatch({ type: 'SET_AI_FILTER_RESULT', payload: errorResult })
        return errorResult
      }
    },
    [state.filters, setFilters, setFilterMode]
  )

  const clearAIFilterResult = useCallback(() => {
    dispatch({ type: 'SET_AI_FILTER_RESULT', payload: null })
  }, [])

  // Tags Management
  const setAvailableTags = useCallback((tags: TagWithUserInfo[]) => {
    dispatch({ type: 'SET_AVAILABLE_TAGS', payload: tags })
  }, [])

  const setTagsLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_TAGS_LOADING', payload: loading })
  }, [])

  const fetchTags = useCallback(async () => {
    try {
      setTagsLoading(true)
      const tags = await buildingTagsService.getAllTags()

      // Transform tags to TagWithUserInfo format
      // Handle both direct tag objects and nested tag structures
      const formattedTags: TagWithUserInfo[] = []

      // Create a map to store unique tags
      const uniqueTagsMap = new Map<string, any>()

      tags.forEach((item: any) => {
        // BuildingTagWithDetails structure has the tag nested
        const tag = item.tag || item

        // Only process if tag has an id and hasn't been added yet
        // Show a fallback label if value is missing
        if (tag && tag.id && !uniqueTagsMap.has(tag.id)) {
          const tagValue = tag.value || tag.name || `Tag ${tag.id}`
          uniqueTagsMap.set(tag.id, {
            id: tag.id,
            value: tagValue,
            color: tag.color || '#4A90E2',
            user: item.user || tag.user || null, // User info might be at item level
          })
        }
      })

      // Convert map to array
      uniqueTagsMap.forEach((tag) => {
        formattedTags.push(tag)
      })

      // If we have no tags with values, log a warning
      if (formattedTags.length === 0 && tags.length > 0) {
        console.warn(
          'Building tags were returned but none had valid values. Check API response.'
        )
      }

      setAvailableTags(formattedTags)
    } catch (error) {
      console.error('Failed to fetch tags:', error)
      setAvailableTags([])
    } finally {
      setTagsLoading(false)
    }
  }, [setAvailableTags, setTagsLoading])

  // Fetch tags when component mounts
  useEffect(() => {
    fetchTags()
  }, [])

  // Trigger search when debouncedFilters change - only in list view and building mode
  useEffect(() => {
    if (viewMode === 'list' && currentMode === 'building') {
      searchBuildings()
    }
  }, [state.debouncedFilters, searchBuildings, viewMode, currentMode])

  // Sync with URL changes (only when URL changes externally)
  useEffect(() => {
    const currentUrlState = JSON.stringify(urlState)

    // Only update if URL actually changed
    if (currentUrlState !== lastUrlStateRef.current) {
      lastUrlStateRef.current = currentUrlState
      isUpdatingFromUrl.current = true

      dispatch({
        type: 'SYNC_FROM_URL',
        payload: {
          filters: urlState.filters, // Use URL filters as the source of truth
          sort: urlState.sort,
          page: urlState.page,
          pageSize: urlState.pageSize,
        },
      })

      // Reset flag after a tick to allow state updates
      setTimeout(() => {
        isUpdatingFromUrl.current = false
      }, 0)
    }
  }, [urlState])

  const contextValue: BuildingsContextType = {
    ...state,
    setBuildings,
    setLoading,
    setError,
    setTotal,
    setCurrentPage,
    setPageSize,
    setFilters,
    setSort,
    setSelectedBuildingId,
    clearFilters,
    searchBuildings,
    loadBuilding,
    updateFilter,
    updateMultipleFilters,
    setFilterMode,
    triggerSearch,
    applyAIFilters,
    clearAIFilterResult,
    setAvailableTags,
    setTagsLoading,
    fetchTags,
  }

  return (
    <BuildingsContext.Provider value={contextValue}>
      {children}
    </BuildingsContext.Provider>
  )
}

export const useBuildings = (): BuildingsContextType => {
  const context = useContext(BuildingsContext)
  if (context === undefined) {
    throw new Error('useBuildings must be used within a BuildingsProvider')
  }
  return context
}
